import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Typography,
} from '@mui/material';
import jsQR from 'jsqr';
import scanDocument from './scan/document';
import {
  challanFromCode, pickChallan, pickExpiry, pickDocumentReference, readDocument, readFields,
} from './scan/ocr';

const ScanDocument = ({ open, onClose, onScanned, kind, paper = false }) => {
  const input = useRef(null); const active = useRef(0); const processing = useRef(false);
  const [busy, setBusy] = useState(''); const [error, setError] = useState('');
  useEffect(() => {
    if (!open) return undefined;
    active.current += 1; setError(''); setBusy('');
    input.current.value = ''; input.current.click();
    return () => { active.current += 1; };
  }, [open]);
  const choose = async (event) => {
    const file = event.target.files?.[0];
    if (!file || processing.current) return;
    processing.current = true;
    const run = active.current; const url = URL.createObjectURL(file);
    setError(''); setBusy('Reading document…');
    try {
      const image = await new Promise((resolve, reject) => {
        const picture = new Image(); picture.onload = () => resolve(picture);
        picture.onerror = () => reject(new Error('Choose a readable document photograph.')); picture.src = url;
      });
      const { canvas } = await scanDocument(image);
      const lines = await readDocument(canvas, (progress) => {
        if (run === active.current) setBusy(progress.stage === 'detecting' ? 'Finding text…' : `Reading line ${progress.done + 1} of ${progress.total}…`);
      });
      if (!lines.length) throw new Error('No readable text found. Try a clearer photograph.');
      setBusy('Matching document details to form fields…');
      const fields = paper ? {} : await readFields(lines);
      const resized = document.createElement('canvas');
      const scale = Math.min(1, 1600 / Math.max(canvas.width, canvas.height));
      resized.width = Math.round(canvas.width * scale); resized.height = Math.round(canvas.height * scale);
      resized.getContext('2d').drawImage(canvas, 0, 0, resized.width, resized.height);
      let imageDataUrl = resized.toDataURL('image/jpeg', 0.7);
      while (imageDataUrl.length > 740000 && resized.width > 400) {
        resized.width = Math.round(resized.width * 0.8); resized.height = Math.round(resized.height * 0.8);
        resized.getContext('2d').drawImage(canvas, 0, 0, resized.width, resized.height);
        imageDataUrl = resized.toDataURL('image/jpeg', 0.6);
      }
      if (imageDataUrl.length > 740000) throw new Error('The photograph is too large. Try a smaller image.');
      let result;
      if (paper) {
        const expiry = pickExpiry(lines);
        result = { imageDataUrl, expiresOn: expiry.from === 'label' ? expiry.expiresOn : '', reference: pickDocumentReference(lines) || undefined };
      } else {
        const pixels = resized.getContext('2d').getImageData(0, 0, resized.width, resized.height);
        const code = jsQR(pixels.data, pixels.width, pixels.height, { inversionAttempts: 'attemptBoth' });
        const picked = pickChallan(lines);
        result = { imageDataUrl, fields, challanNumber: challanFromCode(code?.data || '') || (picked.from === 'label' ? picked.number : '') };
      }
      if (run !== active.current) return;
      onScanned(result); onClose();
    } catch (failure) {
      if (run === active.current) setError(failure.message || 'Could not read the document. Try again.');
    } finally {
      URL.revokeObjectURL(url); processing.current = false;
      if (run === active.current) setBusy('');
    }
  };
  return (
    <>
      <input ref={input} hidden type="file" accept="image/*" capture="environment" onChange={choose} />
      <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
        <DialogTitle>{`Scan and fill ${kind || 'challan'}`}</DialogTitle>
        <DialogContent>
          {busy && <LinearProgress sx={{ mb: 2 }} />}
          {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
          <Typography>{busy || 'Choose or photograph the document. Recognized details fill the form automatically; review them before saving.'}</Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={!!busy} onClick={onClose}>Cancel</Button>
          {!busy && <Button onClick={() => { input.current.value = ''; input.current.click(); }}>Choose document</Button>}
        </DialogActions>
      </Dialog>
    </>
  );
};
export default ScanDocument;
